import React, { useState, useRef, useCallback } from 'react';
import ReactCrop, {
  makeAspectCrop,
  centerCrop,
  convertToPixelCrop,
} from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import './ImageCropper.css';
import AppIcon from '../../../../Components/AppIcon';

// Convert the completed crop into a File (canvas → Blob → File)
const getCroppedFile = (image, crop, fileName) => {
  const canvas = document.createElement('canvas');
  const scaleX = image.naturalWidth / image.width;
  const scaleY = image.naturalHeight / image.height;

  const pixelWidth = Math.max(1, Math.round(crop.width * scaleX));
  const pixelHeight = Math.max(1, Math.round(crop.height * scaleY));

  canvas.width = pixelWidth;
  canvas.height = pixelHeight;

  const ctx = canvas.getContext('2d');
  if (!ctx) return Promise.resolve(null);

  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(
    image,
    crop.x * scaleX,
    crop.y * scaleY,
    crop.width * scaleX,
    crop.height * scaleY,
    0,
    0,
    pixelWidth,
    pixelHeight,
  );

  const safeName = String(fileName || 'cropped.jpg').replace(/\.[^.]+$/, '') || 'cropped';
  return new Promise((resolve) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        resolve(null);
        return;
      }
      resolve(new File([blob], `${safeName}.jpg`, { type: 'image/jpeg', lastModified: Date.now() }));
    }, 'image/jpeg', 0.9);
  });
};

/**
 * ImageCropper Modal
 * Props: src, fileName, aspect, title, onDone(File), onCancel()
 */
const ImageCropper = ({
  src,
  fileName,
  aspect = 4 / 3,
  title = 'Crop Image',
  onDone,
  onCancel,
}) => {
  const imgRef = useRef(null);
  const [crop, setCrop] = useState();
  const [completedCrop, setCompletedCrop] = useState(null);
  const [processing, setProcessing] = useState(false);

  const onImageLoad = useCallback((e) => {
    const { width, height } = e.currentTarget;
    const next = centerCrop(
      makeAspectCrop({ unit: '%', width: 90 }, aspect, width, height),
      width,
      height,
    );
    setCrop(next);
    setCompletedCrop(convertToPixelCrop(next, width, height));
  }, [aspect]);

  const handleDone = async () => {
    if (!imgRef.current || !completedCrop?.width || !completedCrop?.height) return;
    setProcessing(true);
    try {
      const file = await getCroppedFile(imgRef.current, completedCrop, fileName);
      if (file) onDone(file);
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="ic-overlay" onClick={onCancel} role="presentation">
      <div
        className="ic-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <div className="ic-header">
          <span className="ic-title"><AppIcon name="crop" size={16} /> {title}</span>
          <button type="button" className="ic-close" onClick={onCancel} aria-label="Close">
            <AppIcon name="close" size={16} />
          </button>
        </div>

        <div className="ic-body">
          <ReactCrop
            crop={crop}
            onChange={(c) => setCrop(c)}
            onComplete={(c) => setCompletedCrop(c)}
            aspect={aspect}
            minWidth={40}
            minHeight={40}
            keepSelection
          >
            <img
              ref={imgRef}
              src={src}
              alt="Crop preview"
              onLoad={onImageLoad}
              className="ic-image"
            />
          </ReactCrop>
        </div>

        <div className="ic-footer">
          <button type="button" className="ic-btn ic-btn-cancel" onClick={onCancel}>
            Cancel
          </button>
          <button
            type="button"
            className="ic-btn ic-btn-done"
            onClick={handleDone}
            disabled={!completedCrop?.width || processing}
          >
            {processing ? 'Processing…' : 'Use Cropped Image'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ImageCropper;
export const COMPANY_HEADER_CROP_ASPECT = 2 / 1;
